import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { FaCoins } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ADJUST_CRIMCOINS } from "../queries/mutation/rein-tips/mutations";
import { GET_CUSTOM_TIP_HISTORY } from "../queries/mutation/rein-tips/queries";
import { ReinCustomTip } from "../types/ReinCustomTip";
import { timeAgo } from "./pages/broadcasters/community/format";

// Mirrors the server's cap, so the problem shows before submitting.
const MAX_ADJUSTMENT = 100000;
const PRESETS = [100, 500, 1000, 5000];
const RECENT_COUNT = 5;

type Props = {
  userId: string;
  username: string;
  balance: number;
  onAdjusted: () => void;
};

const CrimcoinAdjuster = ({ userId, username, balance, onAdjusted }: Props) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"add" | "remove">("add");
  const [amountText, setAmountText] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  // The mutation returns the new balance straight away; this shows it until
  // the parent's refetch brings the same number in through props.
  const [latestBalance, setLatestBalance] = useState<number | null>(null);

  useEffect(() => {
    setLatestBalance(null);
  }, [balance]);

  const { data: historyData, refetch: refetchHistory } = useQuery(
    GET_CUSTOM_TIP_HISTORY,
    { variables: { userId }, fetchPolicy: "cache-and-network" },
  );
  const recent: ReinCustomTip[] = (
    historyData?.getCustomTipHistory?.customTips ?? []
  ).slice(0, RECENT_COUNT);

  const [adjust, { loading: saving }] = useMutation(ADJUST_CRIMCOINS);

  const current = latestBalance ?? balance;
  const amount = Number(amountText) || 0;
  const signed = mode === "add" ? amount : -amount;
  const next = current + signed;

  const problem =
    amount > MAX_ADJUSTMENT
      ? `Max ${MAX_ADJUSTMENT.toLocaleString()} at a time`
      : mode === "remove" && amount > current
        ? `${username} only has ${current.toLocaleString()} crimcoins`
        : null;

  const handleConfirm = async () => {
    try {
      const { data } = await adjust({
        variables: {
          input: { userId, amount: signed, reason: reason.trim() || null },
        },
      });
      setLatestBalance(data.adjustCrimcoins.balance);
      toast.success(
        `${mode === "add" ? "Added" : "Removed"} ${amount.toLocaleString()} Crim Coins ${
          mode === "add" ? "to" : "from"
        } ${username}`,
      );
      setAmountText("");
      setReason("");
      refetchHistory();
      onAdjusted();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="rounded-box bg-base-300 p-3 border-2 border-primary">
      <div className="flex flex-row justify-between items-center text-primary">
        <span className="font-semibold">Crim Coins</span>
        <FaCoins size={24} />
      </div>
      <div className="divider m-0 divider-primary" />

      <div className="text-center">
        <div className="text-3xl font-bold text-secondary">
          {current.toLocaleString()}
        </div>
        <div className="text-xs text-base-content/60">current balance</div>
      </div>

      <div role="tablist" className="tabs tabs-boxed grid grid-cols-2 mt-3">
        <button
          role="tab"
          aria-selected={mode === "add"}
          className={`tab ${mode === "add" ? "tab-active" : ""}`}
          onClick={() => setMode("add")}
        >
          Add
        </button>
        <button
          role="tab"
          aria-selected={mode === "remove"}
          className={`tab ${mode === "remove" ? "tab-active" : ""}`}
          onClick={() => setMode("remove")}
        >
          Remove
        </button>
      </div>

      <input
        type="text"
        inputMode="numeric"
        value={amountText}
        // Whole numbers only - the server rejects anything else.
        onChange={(e) => setAmountText(e.target.value.replace(/\D/g, ""))}
        placeholder="Amount"
        aria-label="Amount of crimcoins"
        className="input input-bordered w-full mt-2"
      />
      <div className="flex flex-wrap gap-2 mt-2">
        {PRESETS.map((n) => (
          <button
            key={n}
            className={`btn btn-sm ${amount === n ? "btn-primary" : "btn-outline"}`}
            onClick={() => setAmountText(String(n))}
          >
            {n.toLocaleString()}
          </button>
        ))}
      </div>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={200}
        placeholder="Reason (optional - they'll see it)"
        aria-label="Reason"
        className="input input-bordered w-full mt-2"
      />

      {amount > 0 && !problem && (
        <div className="text-sm mt-2 text-center">
          Balance {current.toLocaleString()} →{" "}
          <span
            className={`font-bold ${mode === "add" ? "text-success" : "text-error"}`}
          >
            {next.toLocaleString()}
          </span>
        </div>
      )}
      {problem && (
        <div className="text-xs text-error mt-2 text-center">{problem}</div>
      )}

      <button
        className={`btn w-full mt-3 ${mode === "add" ? "btn-success" : "btn-error"}`}
        disabled={!amount || !!problem || saving}
        onClick={() => setConfirming(true)}
      >
        {mode === "add" ? "Add" : "Remove"}
        {amount ? ` ${amount.toLocaleString()} CC` : ""}
      </button>

      <div className="mt-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold">Recent activity</span>
          <button
            className="btn btn-ghost btn-xs"
            onClick={() => navigate(`/user-custom-tip-history?id=${userId}`)}
          >
            Full history
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="text-xs text-base-content/50 mt-1">No activity yet</div>
        ) : (
          <ul className="mt-1 space-y-1">
            {recent.map((entry, i) => {
              const value = Number(entry.amount);
              return (
                <li
                  key={entry.id ?? i}
                  className="flex justify-between gap-2 text-xs"
                >
                  <span className="truncate text-base-content/70">
                    {entry.description ?? entry.type}
                  </span>
                  <span className="whitespace-nowrap">
                    <span className={value < 0 ? "text-error" : "text-success"}>
                      {value > 0 ? "+" : ""}
                      {value.toLocaleString()}
                    </span>{" "}
                    <span className="text-base-content/50">
                      {timeAgo(entry.createdAt)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirming && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {mode === "add" ? "Add" : "Remove"} {amount.toLocaleString()} Crim
              Coins {mode === "add" ? "to" : "from"} {username}?
            </h3>
            <p className="py-3">
              Balance {current.toLocaleString()} →{" "}
              <span
                className={`font-bold ${mode === "add" ? "text-success" : "text-error"}`}
              >
                {next.toLocaleString()}
              </span>
            </p>
            {reason.trim() && (
              <p className="text-sm">
                Reason: <span className="italic">{reason.trim()}</span>
              </p>
            )}
            <p className="text-sm text-base-content/60 mt-2">
              It's recorded in their history, and they get a Discord DM if
              they've linked Discord.
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setConfirming(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className={`btn ${mode === "add" ? "btn-success" : "btn-error"}`}
                onClick={handleConfirm}
                disabled={saving}
              >
                {saving ? "Saving..." : mode === "add" ? "Add" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrimcoinAdjuster;
